import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { ScheduleOverride } from 'api/locations'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import locationsLocale from 'locales/locations'
import moment from 'moment-timezone'
import React from 'react'

const t = buildTranslate('locations.location_list')

// Wire `date` is a bare YYYY-MM-DD; parse as UTC so the rendered day never
// shifts with the viewer's timezone (same as the legacy
// OrderingManagement/View formatter).
function formatDate(dateStr: string): string {
  return moment.utc(dateStr).format('MMM D, YYYY')
}

// e.g. [{open: '09:00', close: '11:00'}, {open: '13:00', close: '21:00'}]
// → "09:00–11:00 and 13:00–21:00"; three ranges →
// "09:00–11:00, 13:00–17:00 and 18:00–21:00". Comma-separate all but the
// last, which the joiner precedes (reads better than "A and B and C"). An
// empty array means closed all day — the row renders the Closed label
// instead (see ScheduleOverrideRow).
function formatHours(hours: ScheduleOverride['hours']): string {
  const ranges = hours.map(range =>
    t('location_detail.schedule_overrides.hours_range', {
      open: range.open,
      close: range.close,
    })
  )
  if (ranges.length <= 1) return ranges.join('')

  const joiner = t('location_detail.schedule_overrides.hours_joiner')
  const separator = t('location_detail.schedule_overrides.hours_separator')
  return (
    ranges.slice(0, -1).join(separator) + joiner + ranges[ranges.length - 1]
  )
}

// Unknown handoff modes fall back to the raw wire value. The known set is
// checked explicitly because the i18n missing-key "guess" behaviour
// overrides `defaultValue`, silently rendering a guessed lowercase word for
// unmapped keys. Derived from the locale's handoff_mode keys (rather than a
// hand-maintained list) so the two can't drift out of lockstep — adding a
// mode to the locale automatically extends coverage.
const KNOWN_HANDOFF_MODES = Object.keys(
  locationsLocale.location_list.location_detail.schedule_overrides.handoff_mode
)

function formatHandoffMode(mode: string): string {
  const key = mode.toLowerCase()
  if (!KNOWN_HANDOFF_MODES.includes(key)) return mode

  return t(`location_detail.schedule_overrides.handoff_mode.${key}`)
}

const ScheduleOverrideRow = ({ override }: { override: ScheduleOverride }) => {
  const [css] = useStyletron()
  return (
    <div
      className={`d-flex justify-content-between align-items-start p-m mb-s ${css(
        {
          backgroundColor: palette.white,
          border: `1px solid ${palette.grey20}`,
          borderRadius: '4px',
        }
      )}`}
    >
      <div>
        <p
          className={`bold body-text-3 mb-xxs ${css({
            color: palette.grey90,
          })}`}
        >
          {formatDate(override.date)}{' '}
          {t('location_detail.schedule_overrides.header_separator')}{' '}
          {formatHandoffMode(override.handoff_mode)}
        </p>
        {override.description && (
          <p className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}>
            {override.description}
          </p>
        )}
      </div>
      <p className={`body-text-4 mb-0 ml-m ${css({ color: palette.grey70 })}`}>
        {override.hours.length > 0
          ? formatHours(override.hours)
          : t('location_detail.schedule_overrides.closed')}
      </p>
    </div>
  )
}

const ScheduleOverrides = ({
  overrides,
}: {
  overrides: ScheduleOverride[]
}) => {
  const [css] = useStyletron()
  return (
    <section className="mb-xl">
      <Text.Header4 className={`mb-xs ${css({ color: palette.grey90 })}`}>
        {t('location_detail.schedule_overrides.title')}
      </Text.Header4>
      <p className={`body-text-3 mb-m ${css({ color: palette.grey70 })}`}>
        {t('location_detail.schedule_overrides.description')}
      </p>
      {overrides.length === 0 ? (
        <div
          className={`d-flex align-items-center p-m ${css({
            backgroundColor: palette.belizeHole10,
            borderRadius: '4px',
          })}`}
        >
          <i
            className={`fa fa-info-circle mr-m ${css({
              color: palette.belizeHole,
              fontSize: '20px',
            })}`}
            aria-hidden="true"
          />
          <p className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}>
            {t('location_detail.schedule_overrides.empty')}
          </p>
        </div>
      ) : (
        overrides.map((o, idx) => (
          <ScheduleOverrideRow
            key={`${o.date}-${o.handoff_mode}-${idx}`}
            override={o}
          />
        ))
      )}
    </section>
  )
}

export default ScheduleOverrides
